// ============================================================================
// CHANGO V5.0 — Ops System Config panel (admin-only)
// ============================================================================
// Direct-flip controls for the small set of `system_config` keys that are
// safe to change without two-person review. Backed by:
//
//   GET  /api/ops/system-config                      — dump + bounds
//   PUT  /api/ops/system-config/billing-mode                  { billing_mode }
//   PUT  /api/ops/system-config/trial-length-days             { trial_length_days }
//   PUT  /api/ops/system-config/workspace-archive-after-days  { workspace_archive_after_days }
//
// §11.1 billing_mode — public-facing switch (free_preview ↔ paid_trial). Flip
//   only after UEDA has confirmed the /register banner copy is ready.
// §9.6.5 trial_length_days — 1..90 days; default 7. Per-tenant overrides live
//   on the workspace detail page, not here.
// §9.6.6 workspace_archive_after_days — 1..365 days; default 30. Drives the
//   nightly soft-archive sweep's threshold for `trial_expired`/`frozen` rows.
//
// Every write produces an ops_action_log row on the server — no client-side
// audit needed.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

// ---- small presentational helpers ------------------------------------------

function Banner({ kind, children }) {
  if (!children) return null;
  const tone = {
    error:   'border-rose-500/40 bg-rose-900/30 text-rose-200',
    ok:      'border-emerald-500/40 bg-emerald-900/30 text-emerald-200',
    warn:    'border-amber-500/40 bg-amber-900/30 text-amber-200',
    info:    'border-slate-500/40 bg-slate-800/60 text-slate-200',
  }[kind || 'info'];
  return (
    <div className={`mb-4 rounded-md border px-4 py-2 text-sm ${tone}`}>
      {children}
    </div>
  );
}

function BoundsHint({ min, max, current }) {
  return (
    <span className="mt-1 block text-xs text-slate-400">
      允许范围 <code>{min}</code> – <code>{max}</code>
      {current != null && (
        <> · 当前值 <code className="ml-1 rounded bg-slate-700 px-1">{current}</code></>
      )}
    </span>
  );
}

// ---- data loader -----------------------------------------------------------

function useSystemConfig() {
  const [state, setState] = useState({
    entries: null,
    allowed_billing_modes: [],
    trial_bounds: null,
    archive_bounds: null,
    loading: true,
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((x) => x + 1), []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await api.get('/api/ops/system-config');
        if (cancelled) return;
        setState({
          entries: res?.entries || {},
          allowed_billing_modes: Array.isArray(res?.allowed_billing_modes)
            ? res.allowed_billing_modes
            : [],
          trial_bounds: res?.trial_length_days_bounds || null,
          archive_bounds: res?.workspace_archive_after_days_bounds || null,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: err instanceof HttpError
            ? `${err.code}: ${err.message}`
            : (err?.message || '加载失败'),
        }));
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  return { ...state, reload };
}

// ---- billing_mode card -----------------------------------------------------

function BillingModeCard({ current, allowed, onSaved }) {
  const [value, setValue] = useState(current ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { setValue(current ?? ''); setErr(null); }, [current]);

  const changed = value !== (current ?? '');
  const submit = async () => {
    if (!changed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.put('/api/ops/system-config/billing-mode', {
        billing_mode: value,
      });
      onSaved?.(res?.billing_mode ?? value);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '保存失败'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="text-base font-semibold">计费模式 <code className="text-xs text-slate-400">billing_mode</code></h3>
        <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">§11.1</span>
      </header>

      <p className="mb-3 text-xs text-slate-400">
        <code>free_preview</code>：注册即可试用；公开顶栏显示"免费预览"横幅。
        <code>paid_trial</code>：注册进入 7 天试用，到期转付费。D+6（{' '}
        <span className="font-mono">2026-06-01</span>）切换一次。
      </p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="block flex-1">
          <span className="text-sm font-medium text-slate-200">模式</span>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            className="mt-1 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-chango-500 focus:outline-none"
          >
            {allowed.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={submit}
          disabled={!changed || busy}
          className="rounded-md bg-chango-600 px-4 py-2 text-sm font-semibold text-white hover:bg-chango-700 disabled:opacity-50"
        >
          {busy ? '保存中…' : '保存'}
        </button>
      </div>

      {err && (
        <div className="mt-3 rounded border border-rose-500/40 bg-rose-900/30 px-3 py-2 text-xs text-rose-200">
          {err}
        </div>
      )}
    </section>
  );
}

// ---- numeric-threshold card (reused for both trial + archive) --------------

function NumericConfigCard({
  title, code, section, blurb, path, payloadKey, current, bounds,
  onSaved,
}) {
  const min = bounds?.min ?? 1;
  const max = bounds?.max ?? 365;
  const def = bounds?.default ?? null;
  const [value, setValue] = useState(current == null ? '' : String(current));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setValue(current == null ? '' : String(current));
    setErr(null);
  }, [current]);

  const parsed = Number.parseInt(value, 10);
  const numOk =
    Number.isInteger(parsed) &&
    String(parsed) === String(value).trim() &&
    parsed >= min && parsed <= max;
  const changed = numOk && parsed !== Number(current);

  const submit = async () => {
    if (!numOk || !changed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.put(path, { [payloadKey]: parsed });
      onSaved?.(res?.[payloadKey] ?? parsed);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '保存失败'));
    } finally {
      setBusy(false);
    }
  };

  const resetToDefault = () => { if (def != null) setValue(String(def)); };

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-800/50 p-5">
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="text-base font-semibold">
          {title} <code className="text-xs text-slate-400">{code}</code>
        </h3>
        <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">{section}</span>
      </header>

      <p className="mb-3 text-xs text-slate-400">{blurb}</p>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="block flex-1">
          <span className="text-sm font-medium text-slate-200">天数</span>
          <input
            type="number"
            inputMode="numeric"
            step="1"
            min={min}
            max={max}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            className="mt-1 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-mono text-slate-100 focus:border-chango-500 focus:outline-none"
            placeholder={def != null ? `默认 ${def}` : ''}
          />
          <BoundsHint min={min} max={max} current={current} />
          {!numOk && value !== '' && (
            <span className="mt-1 block text-xs text-rose-400">
              请输入 {min} – {max} 之间的整数
            </span>
          )}
        </label>
        <div className="flex gap-2">
          {def != null && (
            <button
              type="button"
              onClick={resetToDefault}
              disabled={busy || Number(value) === def}
              className="rounded-md border border-slate-600 px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 disabled:opacity-40"
            >
              重置为默认 {def}
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!changed || !numOk || busy}
            className="rounded-md bg-chango-600 hover:bg-chango-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>

      {err && (
        <div className="mt-3 rounded border border-rose-500/40 bg-rose-900/30 px-3 py-2 text-xs text-rose-200">
          {err}
        </div>
      )}
    </section>
  );
}

// ---- raw entries table (read-only) -----------------------------------------

function RawEntriesTable({ entries }) {
  const keys = useMemo(
    () => Object.keys(entries || {}).sort(),
    [entries]
  );
  if (keys.length === 0) {
    return (
      <div className="rounded border border-slate-700 bg-slate-800/30 p-4 text-sm text-slate-400">
        （暂无配置项）
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-800/40">
      <table className="w-full text-sm">
        <thead className="bg-slate-800/80 text-xs uppercase text-slate-400">
          <tr>
            <th className="px-4 py-2 text-left">键</th>
            <th className="px-4 py-2 text-left">值</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const v = entries[k];
            const isObj = v !== null && typeof v === 'object';
            return (
              <tr key={k} className="border-t border-slate-700/60">
                <td className="px-4 py-2 font-mono text-xs text-slate-300">{k}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-100">
                  {v == null
                    ? <span className="text-slate-500">null</span>
                    : isObj
                      ? <pre className="whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>
                      : String(v)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---- page ------------------------------------------------------------------

export default function OpsSystemConfig() {
  const { user } = useAuth();
  const {
    entries, allowed_billing_modes, trial_bounds, archive_bounds,
    loading, error, reload,
  } = useSystemConfig();
  const [okMsg, setOkMsg] = useState(null);

  const flashOk = useCallback((msg) => {
    setOkMsg(msg);
    setTimeout(() => setOkMsg((m) => (m === msg ? null : m)), 5000);
  }, []);

  // Phase 3 RoleGate-B: owner ⊃ admin. owner 标记在 is_owner 字段（platform_ownership join, §ST.10 Plan B）
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.is_owner === true;

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h1 className="text-xl font-semibold">403 — 仅平台管理员可访问</h1>
          <p className="mt-2 text-sm text-slate-400">
            你以 <code>{user?.role || 'anonymous'}</code> 身份登录，无权查看此页。
          </p>
          <Link to="/ops/dashboard" className="mt-6 inline-block text-sm text-chango-300 underline">
            返回控制台
          </Link>
        </div>
      </div>
    );
  }

  const billingMode = entries?.billing_mode ?? null;
  const trialDays = entries?.trial_length_days ?? null;
  const archiveDays = entries?.workspace_archive_after_days ?? null;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">系统配置</h1>
            <p className="text-xs text-slate-400">
              {user?.email} · {user?.role} · 所有修改都会写入{' '}
              <code>ops_action_log</code>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/ops/dashboard"
              className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
            >
              返回
            </Link>
            <button
              type="button"
              onClick={reload}
              className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700"
            >
              刷新
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        {loading && (
          <div className="rounded border border-slate-700 bg-slate-800/40 px-4 py-6 text-center text-sm text-slate-400">
            正在加载配置…
          </div>
        )}
        {error && <Banner kind="error">{error}</Banner>}
        {okMsg && <Banner kind="ok">{okMsg}</Banner>}

        {!loading && !error && (
          <div className="grid grid-cols-1 gap-5">
            <BillingModeCard
              current={billingMode}
              allowed={allowed_billing_modes}
              onSaved={(v) => { flashOk(`已保存 billing_mode = ${v}`); reload(); }}
            />
            <NumericConfigCard
              title="试用期长度"
              code="trial_length_days"
              section="§9.6.5"
              blurb="新注册工作区在 pending_review→active 后进入此天数的试用。UEDA 可为单租户覆盖；此处是全站默认。"
              path="/api/ops/system-config/trial-length-days"
              payloadKey="trial_length_days"
              current={trialDays == null ? null : Number(trialDays)}
              bounds={trial_bounds}
              onSaved={(v) => { flashOk(`已保存 trial_length_days = ${v}`); reload(); }}
            />
            <NumericConfigCard
              title="软归档阈值"
              code="workspace_archive_after_days"
              section="§9.6.6"
              blurb="夜间归档任务会将在 trial_expired / frozen 状态下停留超过此天数的工作区迁入软归档；默认 30 天。"
              path="/api/ops/system-config/workspace-archive-after-days"
              payloadKey="workspace_archive_after_days"
              current={archiveDays == null ? null : Number(archiveDays)}
              bounds={archive_bounds}
              onSaved={(v) => { flashOk(`已保存 workspace_archive_after_days = ${v}`); reload(); }}
            />

            <section className="mt-4">
              <h2 className="mb-3 text-sm font-semibold text-slate-300">
                所有 system_config 键（只读）
              </h2>
              <RawEntriesTable entries={entries} />
              <p className="mt-2 text-xs text-slate-500">
                上方卡片覆盖常见开关；其他键仅供查阅，改动走数据库或两人制 review_request 流程。
              </p>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
